import { useCallback, useMemo, useState } from 'react';

import { mergeFilterUpdate } from '#/modules/commonComponents/table/listFilterKeys';
import { CUSTOMER_ORDER_STATUS } from '#/modules/crmMgmt/customerOrder/helper/customerOrderStatus';

const DEFAULT_PAGE_SIZE = 10;

/** 可建銷貨單的客訂狀態（APPROVED 起）。 */
export const CUSTOMER_ORDER_PICKER_BASE_FILTERS = {
  status: [
    CUSTOMER_ORDER_STATUS.APPROVED,
    CUSTOMER_ORDER_STATUS.PARTIALLY_SHIPPED,
    CUSTOMER_ORDER_STATUS.SHIPPED,
  ],
};

/**
 * 客訂選取 Dialog 本地 listQuery（不寫 URL）。status 鎖定可建 SO 態。
 */
function useCustomerOrderPickerListQuery() {
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [keyword, setKeywordState] = useState('');
  const [sort, setSortState] = useState([]);
  const [userFilters, setUserFilters] = useState({});

  const lockedFilterKeys = useMemo(
    () => new Set(Object.keys(CUSTOMER_ORDER_PICKER_BASE_FILTERS)),
    [],
  );

  const listQuery = useMemo(() => ({
    page,
    pageSize,
    keyword,
    sort,
    filters: {
      ...CUSTOMER_ORDER_PICKER_BASE_FILTERS,
      ...userFilters,
    },
  }), [page, pageSize, keyword, sort, userFilters]);

  const setPage = useCallback((nextPage) => {
    setPageState(nextPage);
  }, []);

  const setPageSize = useCallback((nextPageSize) => {
    setPageSizeState(nextPageSize);
    setPageState(1);
  }, []);

  const setKeyword = useCallback((nextKeyword) => {
    setKeywordState(nextKeyword ?? '');
    setPageState(1);
  }, []);

  const setFilter = useCallback((key, value) => {
    if (lockedFilterKeys.has(key)) {
      return;
    }
    setUserFilters((prev) => mergeFilterUpdate(prev, key, value));
    setPageState(1);
  }, [lockedFilterKeys]);

  const setSort = useCallback((nextSort) => {
    setSortState(nextSort ?? []);
    setPageState(1);
  }, []);

  const removeSortAt = useCallback((index) => {
    setSortState((prev) => (prev ?? []).filter((_, sortIndex) => sortIndex !== index));
    setPageState(1);
  }, []);

  const resetQuery = useCallback(() => {
    setPageState(1);
    setPageSizeState(DEFAULT_PAGE_SIZE);
    setKeywordState('');
    setSortState([]);
    setUserFilters({});
  }, []);

  return {
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetQuery,
  };
}

export default useCustomerOrderPickerListQuery;
